import { useEffect, useState } from "preact/hooks";
import {
	canStillSend,
	formatCountdown,
	phoneDigitCount,
	type HandoffSendState,
	handoffSendStateAfter,
	requestHandoffSend,
	secondsUntilSend,
} from "@/shared/handoff-send";
import { handoffSendCopy, phoneFieldStart } from "@/shared/handoff-send-copy";
import { handoffLanguage } from "@/shared/whatsapp-handoff";

/**
 * The hosted chat page's "or get a message" (PLT-2165): the same form and the
 * same rules as the floating widget's, in the page's own styles. The page
 * holds the send state, so every card on it shows the same timer.
 */
export function HandoffSendForm(props: {
	chatUrl: string;
	widgetId: string;
	threadId: string | null;
	callingCode: string | null;
	language: string;
	companyName: string;
	primaryColor: string;
	state: HandoffSendState | null;
	onState: (next: HandoffSendState) => void;
}) {
	const copy = handoffSendCopy(props.language);
	const [phone, setPhone] = useState(() => phoneFieldStart(props.callingCode));
	const [consent, setConsent] = useState(false);
	const [sending, setSending] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [, setTick] = useState(0);
	const waitSeconds = secondsUntilSend(props.state, props.threadId);

	useEffect(() => {
		if (waitSeconds <= 0) return;
		const timer = setInterval(() => setTick((t) => t + 1), 1000);
		return () => clearInterval(timer);
	}, [waitSeconds > 0]);

	const threadId = props.threadId;
	if (!threadId) return null;

	if (!canStillSend(props.state, threadId)) {
		return (
			<p className="rounded-lg border border-[#25d366] bg-[#25d366]/10 px-3 py-2 text-xs leading-snug text-[#12653b]">
				{copy.didNotGetIt}
			</p>
		);
	}

	const sentHere =
		props.state && props.state.threadId === threadId ? props.state.sentTo : null;
	const hasDigits = phoneDigitCount(phone) >= 6;
	const consented = consent || Boolean(sentHere);
	const disabled = sending || !consented || !hasDigits || waitSeconds > 0;

	const submit = async (event: Event) => {
		event.preventDefault();
		if (disabled) return;
		setSending(true);
		setError(null);
		const result = await requestHandoffSend({
			chatUrl: props.chatUrl,
			widgetId: props.widgetId,
			threadId,
			phone,
			language: handoffLanguage(props.language),
		});
		props.onState(handoffSendStateAfter(props.state, threadId, result));
		if (!result.ok) {
			if (result.reason === "invalid_phone") setError(copy.invalidPhone);
			else if (result.reason === "failed") setError(copy.failed);
		}
		setSending(false);
	};

	return (
		<form className="flex flex-col gap-2" onSubmit={submit}>
			{sentHere ? (
				<p
					role="status"
					className="rounded-lg border border-[#25d366] bg-[#25d366]/10 px-3 py-2 text-xs leading-snug text-[#12653b]"
				>
					{copy.sentTo(sentHere)} <strong>{copy.tapContinue}</strong>
				</p>
			) : (
				<p className="flex items-center gap-2 text-xs text-gray-500 before:h-px before:flex-1 before:bg-gray-200 after:h-px after:flex-1 after:bg-gray-200">
					{copy.orGetMessage}
				</p>
			)}
			<div className="flex gap-2">
				<input
					type="tel"
					dir="ltr"
					autoComplete="tel"
					aria-label={copy.phoneLabel}
					value={phone}
					onInput={(e) => setPhone((e.currentTarget as HTMLInputElement).value)}
					className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2"
					style={{ "--tw-ring-color": props.primaryColor } as Record<string, string>}
				/>
				<button
					type="submit"
					disabled={disabled}
					className="shrink-0 rounded-lg px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
					style={{ backgroundColor: props.primaryColor }}
				>
					{sending
						? copy.sending
						: waitSeconds > 0
							? copy.resendIn(formatCountdown(waitSeconds))
							: copy.send}
				</button>
			</div>
			{error && (
				<p role="alert" className="text-xs text-red-600">
					{error}
				</p>
			)}
			{!sentHere && (
				<label className="flex cursor-pointer items-start gap-2 text-xs leading-snug text-gray-500">
					<input
						type="checkbox"
						checked={consent}
						onChange={(e) => setConsent((e.currentTarget as HTMLInputElement).checked)}
						className="mt-0.5"
						style={{ accentColor: props.primaryColor }}
					/>
					<span>{copy.consent(props.companyName)}</span>
				</label>
			)}
		</form>
	);
}
